/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function() {
  var PIXEL_ID = '758n4qUmwQD9CgAFjGJNzB';
  var SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
  var SEARCH_STORAGE_KEY = 'lw:oai:last-search';
  var SEARCH_STORAGE_TTL = 15000;
  var seenEvents = {};
  var recentCartEvents = {};

  function cleanPayload(payload) {
    var cleaned = {};

    Object.keys(payload || {}).forEach(function(key) {
      var value = payload[key];

      if (value === null || value === undefined || value === '') {
        return;
      }

      if (Array.isArray(value) && value.length === 0) {
        return;
      }

      cleaned[key] = value;
    });

    return cleaned;
  }

  function getThemeVariables() {
    return window.themeVariables || {};
  }

  function getOpenAIAdsContext() {
    return getThemeVariables().openAIAds || {};
  }

  function getCurrency() {
    var themeVariables = getThemeVariables();
    var adsContext = getOpenAIAdsContext();

    return adsContext.currency || (themeVariables.settings && themeVariables.settings.cartCurrency) || 'USD';
  }

  function toAmount(value) {
    var amount = Number(value);

    if (!Number.isFinite(amount)) {
      return undefined;
    }

    return Math.round(amount * 100) / 100;
  }

  function centsToAmount(value) {
    var cents = Number(value);

    if (!Number.isFinite(cents)) {
      return undefined;
    }

    return toAmount(cents / 100);
  }

  function markRecentCartEvent(key) {
    recentCartEvents[key] = Date.now();
  }

  function isRecentCartEvent(key) {
    return recentCartEvents[key] && (Date.now() - recentCartEvents[key]) < 2500;
  }

  function initOpenAIPixel() {
    if (window.__lastwolfOpenAIPixelInitialized) {
      return;
    }

    if (!window.oaiq) {
      var queue = function() {
        queue.q.push(arguments);
      };

      queue.q = [];
      window.oaiq = queue;

      var script = document.createElement('script');
      script.async = true;
      script.src = SDK_URL;
      document.head.appendChild(script);
    }

    window.oaiq('init', {
      pixelId: PIXEL_ID,
      debug: false
    });

    window.__lastwolfOpenAIPixelInitialized = true;
  }

  function measure(eventName, payload, dedupeKey) {
    if (dedupeKey && seenEvents[dedupeKey]) {
      return;
    }

    initOpenAIPixel();

    if (dedupeKey) {
      seenEvents[dedupeKey] = true;
    }

    var basePayload = {
      type: 'customer_action',
      currency: getCurrency(),
      page_type: getOpenAIAdsContext().pageType,
      source: 'theme',
      store_domain: window.location.hostname
    };

    Object.keys(payload || {}).forEach(function(key) {
      basePayload[key] = payload[key];
    });

    window.oaiq('measure', eventName, cleanPayload(basePayload));
  }

  function trackProductView() {
    var adsContext = getOpenAIAdsContext();
    var product = adsContext.product;

    if (adsContext.pageType !== 'product' || !product || !product.id) {
      return;
    }

    measure(
      'view_item',
      {
        amount: toAmount(product.price),
        product_id: String(product.id),
        variant_id: product.variantId ? String(product.variantId) : undefined,
        product_title: product.title,
        variant_title: product.variantTitle,
        vendor: product.vendor,
        handle: product.handle
      },
      'view_item:' + product.id + ':' + (product.variantId || '')
    );
  }

  function storeSearchSubmission(term) {
    try {
      sessionStorage.setItem(
        SEARCH_STORAGE_KEY,
        JSON.stringify({
          term: term.toLowerCase(),
          ts: Date.now()
        })
      );
    } catch (error) {
      // Ignore storage failures in privacy modes.
    }
  }

  function getRecentSearchSubmission() {
    try {
      var rawValue = sessionStorage.getItem(SEARCH_STORAGE_KEY);

      if (!rawValue) {
        return null;
      }

      return JSON.parse(rawValue);
    } catch (error) {
      return null;
    }
  }

  function trackSearch(term, origin, resultsCount) {
    var normalizedTerm = (term || '').trim();

    if (!normalizedTerm) {
      return;
    }

    measure(
      'search',
      {
        search_term: normalizedTerm,
        search_origin: origin,
        results_count: typeof resultsCount === 'number' ? resultsCount : undefined
      },
      'search:' + origin + ':' + normalizedTerm.toLowerCase()
    );
  }

  function bindSearchForms() {
    var themeVariables = getThemeVariables();
    var searchUrl = themeVariables.routes && themeVariables.routes.searchUrl ? themeVariables.routes.searchUrl : '/search';
    var forms = document.querySelectorAll('form[action*="' + searchUrl + '"], #predictive-search-form, .main-search__form');

    Array.prototype.forEach.call(forms, function(form) {
      if (form.dataset.openAiAdsBound === 'true') {
        return;
      }

      form.dataset.openAiAdsBound = 'true';

      form.addEventListener('submit', function() {
        var queryInput = form.querySelector('[name="q"]');
        var term = queryInput && queryInput.value ? queryInput.value.trim() : '';
        var origin = form.id === 'predictive-search-form' ? 'predictive_search' : 'search_form';

        if (!term) {
          return;
        }

        storeSearchSubmission(term);
        trackSearch(term, origin);
      });
    });
  }

  function trackSearchResultsPage() {
    var adsContext = getOpenAIAdsContext();
    var search = adsContext.search || {};
    var term = typeof search.terms === 'string' ? search.terms.trim() : '';

    if (adsContext.pageType !== 'search' || !search.performed || !term) {
      return;
    }

    var recentSearch = getRecentSearchSubmission();
    var isDuplicateSubmission = recentSearch
      && recentSearch.term === term.toLowerCase()
      && (Date.now() - recentSearch.ts) < SEARCH_STORAGE_TTL;

    if (isDuplicateSubmission) {
      return;
    }

    trackSearch(term, 'results_page', Number(search.resultsCount) || 0);
  }

  function trackAddedToCart(event) {
    var variant = event && event.detail ? event.detail.variant : null;
    var productContext = getOpenAIAdsContext().product || {};
    var form = event && event.target && event.target.querySelector ? event.target : null;
    var quantityInput = form ? form.querySelector('[name="quantity"]') : null;
    var quantity = quantityInput ? Number(quantityInput.value) || 1 : 1;

    if (!variant || !variant.id) {
      return;
    }

    trackAddedVariant(variant, {
      quantity: quantity,
      productContext: productContext,
      source: 'variant_added_event'
    });
  }

  function trackAddedVariant(variant, options) {
    var productContext = options && options.productContext ? options.productContext : (getOpenAIAdsContext().product || {});
    var quantity = options && options.quantity ? Number(options.quantity) || 1 : 1;
    var unitAmount = variant ? centsToAmount(variant.price) : undefined;
    var totalAmount = typeof unitAmount === 'number' ? toAmount(unitAmount * quantity) : undefined;
    var dedupeKey;

    if (!variant || !variant.id) {
      return;
    }

    dedupeKey = String(variant.id) + ':' + quantity;

    if (isRecentCartEvent(dedupeKey)) {
      return;
    }

    markRecentCartEvent(dedupeKey);

    measure('items_added', {
      type: 'contents',
      amount: totalAmount,
      quantity: quantity,
      product_id: variant.product_id ? String(variant.product_id) : (productContext.id ? String(productContext.id) : undefined),
      variant_id: String(variant.id),
      product_title: variant.product_title || variant.name || productContext.title,
      variant_title: variant.public_title || variant.title,
      vendor: variant.vendor || productContext.vendor,
      source_context: options && options.source ? options.source : 'theme'
    }, 'items_added:' + dedupeKey);
  }

  function installCartRequestTracking() {
    var originalFetch = window.fetch;
    var cartAddPath = ((getThemeVariables().routes || {}).cartAddUrl || '/cart/add').replace(/^\//, '');

    if (window.__lastwolfOpenAIFetchWrapped || typeof originalFetch !== 'function') {
      return;
    }

    window.fetch = function(input, init) {
      var requestUrl = typeof input === 'string' ? input : (input && input.url ? input.url : '');
      var fetchPromise = originalFetch.apply(this, arguments);

      fetchPromise.then(function(response) {
        var normalizedUrl = requestUrl || (response && response.url) || '';
        var isCartAddRequest = normalizedUrl.indexOf(cartAddPath) !== -1 || normalizedUrl.indexOf('/cart/add.js') !== -1;

        if (!isCartAddRequest || !response || !response.ok) {
          return;
        }

        response.clone().json().then(function(payload) {
          var addedItem = payload && payload.items ? payload.items[0] : payload;
          var bodyQuantity = 1;

          try {
            if (init && init.body && typeof init.body.get === 'function') {
              bodyQuantity = Number(init.body.get('quantity')) || 1;
            }
          } catch (error) {
            bodyQuantity = 1;
          }

          trackAddedVariant(addedItem, {
            quantity: bodyQuantity,
            source: 'cart_add_fetch'
          });
        }).catch(function() {
          // Ignore non-JSON responses.
        });
      }).catch(function() {
        // Ignore network failures for tracking.
      });

      return fetchPromise;
    };

    window.__lastwolfOpenAIFetchWrapped = true;
  }

  function bootOpenAIAdsTracking() {
    initOpenAIPixel();
    installCartRequestTracking();
    trackProductView();
    bindSearchForms();
    trackSearchResultsPage();
    document.addEventListener('variant:added', trackAddedToCart);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootOpenAIAdsTracking);
  } else {
    bootOpenAIAdsTracking();
  }
})();
